import { Box, Stack } from "@mui/material";
import { DateField } from "components/Forms/DateField";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import {
  FacilityPickerFragment,
  StateWasteStreamRegistrationStatus,
} from "generated-graphql/graphql";
import { Control } from "react-hook-form";
import { WasteCodesField } from "routes/Customer/Waste/Submissions/Stepper/steps/FilingNotifications/WasteCodesField";

import { STATUS_SELECT_ITEMS } from "./statusSelectItems";

// One form shape for both the create dialog and the registration detail
// drawer's edit surface so they can share the field layout below. `facility`
// is only used by create (the edit surface keeps it null and renders its own
// header instead).
export type RegistrationFormValues = {
  facility: FacilityPickerFragment | null;
  stateRegisteredId: string;
  status: StateWasteStreamRegistrationStatus;
  name: string;
  companyDescription: string;
  federalWasteCodes: string[];
  formCodes: string[];
  sourceCode: string;
  dotId: string;
  dotDescription: string;
  registeredAt: string | null;
  closedAt: string | null;
};

/**
 * Shared registration field layout — everything except the create dialog's
 * facility picker and the detail drawer's header — row-grouped for
 * compactness. Used by both CreateRegistrationDialog and
 * RegistrationDetailDrawer.
 */
export function RegistrationFormFields({
  control,
  disabled = false,
}: {
  control: Control<RegistrationFormValues>;
  disabled?: boolean;
}) {
  return (
    <>
      <Stack direction="row" spacing={2}>
        <Box flex={1} minWidth={0}>
          <FormTextField
            name="stateRegisteredId"
            label="State Registered ID"
            control={control}
            disabled={disabled}
          />
        </Box>
        <Box flex={1} minWidth={0}>
          <FormSelect
            name="status"
            label="Status"
            control={control}
            selectItems={STATUS_SELECT_ITEMS}
            clearable={false}
            disabled={disabled}
            rules={{ required: true }}
          />
        </Box>
      </Stack>
      <FormTextField name="name" label="Name" control={control} disabled={disabled} />
      <FormTextField
        name="companyDescription"
        label="Company Description"
        control={control}
        disabled={disabled}
      />
      <WasteCodesField
        name="federalWasteCodes"
        label="Federal Waste Codes"
        control={control}
        disabled={disabled}
      />
      <Stack direction="row" spacing={2}>
        <Box flex={1} minWidth={0}>
          <WasteCodesField
            name="formCodes"
            label="Form Codes"
            control={control}
            disabled={disabled}
          />
        </Box>
        <Box flex={1} minWidth={0}>
          <FormTextField
            name="sourceCode"
            label="Source Code"
            control={control}
            disabled={disabled}
          />
        </Box>
      </Stack>
      <Stack direction="row" spacing={2}>
        <Box flex={1} minWidth={0}>
          <FormTextField name="dotId" label="DOT ID" control={control} disabled={disabled} />
        </Box>
        <Box flex={1} minWidth={0}>
          <FormTextField
            name="dotDescription"
            label="DOT Description"
            control={control}
            disabled={disabled}
          />
        </Box>
      </Stack>
      <Stack direction="row" spacing={2}>
        <Box flex={1} minWidth={0}>
          <DateField
            name="registeredAt"
            label="Registered At"
            control={control}
            disabled={disabled}
          />
        </Box>
        <Box flex={1} minWidth={0}>
          <DateField name="closedAt" label="Closed At" control={control} disabled={disabled} />
        </Box>
      </Stack>
    </>
  );
}
